import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faImage, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './CartPreviewModal.css';

const debounce = (fn, delay = 250) => {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), delay);
    };
};

const CartPreviewModal = ({
    cart,
    products,
    onClose,
    onConfirm,
    creatingOrder,
    onUpdateCart,
    selectedProducts,
    onUpdateSelectedProducts,
    catalogId,
    getAvailableQuantities,
    getPriceForQuantity
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [editedQuantities, setEditedQuantities] = useState({});
    const modalRef = useRef(null);
    const portalContainerRef = useRef(null);

    // Crear contenedor del portal al montar
    useEffect(() => {
        const container = document.createElement('div');
        container.id = 'cart-preview-modal-portal';
        
        try {
            document.body.appendChild(container);
            portalContainerRef.current = container;
        } catch (error) {
            console.error('Error al crear contenedor del portal:', error);
        }

        return () => {
            // Limpiar el contenedor al desmontar
            try {
                if (portalContainerRef.current && document.body.contains(portalContainerRef.current)) {
                    document.body.removeChild(portalContainerRef.current);
                }
            } catch (error) {
                console.error('Error al limpiar contenedor del portal:', error);
            }
            portalContainerRef.current = null;
        };
    }, []);

    // Expandir items del cart que tienen múltiples combinaciones
    const expandedCartItems = useMemo(() => {
        const items = [];

        cart.forEach(cartItem => {
            // Si el item ya tiene rowId, es un item individual (nuevo formato)
            if (cartItem.rowId) {
                items.push({
                    id: cartItem.productId, // Ya es único
                    productId: cartItem.originalProductId || cartItem.productId, // Usar originalProductId para buscar el producto
                    productName: cartItem.productName,
                    productImageUrl: cartItem.productImageUrl,
                    color: cartItem.colorName || '',
                    size: cartItem.sizeName || '',
                    quantity: cartItem.quantity,
                    combinationIndex: 0,
                    unitPrice: cartItem.unitPrice,
                    rowId: cartItem.rowId,
                    isIndividualItem: true
                });
            } else {
                // Formato antiguo con punto y coma
                const colors = cartItem.colorName ? cartItem.colorName.split(';') : [''];
                const sizes = cartItem.sizeName ? cartItem.sizeName.split(';') : [''];
                const quantities = cartItem.individualQuantities ?
                    cartItem.individualQuantities.split(';') :
                    [cartItem.quantity.toString()];

                const maxLength = Math.max(colors.length, sizes.length, quantities.length);

                for (let i = 0; i < maxLength; i++) {
                    const color = colors[i] || '';
                    const size = sizes[i] || '';
                    const qty = parseInt(quantities[i]) || 0;

                    items.push({
                        id: `${cartItem.productId}_${i}`,
                        productId: cartItem.productId,
                        productName: cartItem.productName,
                        productImageUrl: cartItem.productImageUrl,
                        color: color,
                        size: size,
                        quantity: qty,
                        combinationIndex: i,
                        unitPrice: cartItem.unitPrice,
                        isIndividualItem: false
                    });
                }
            }
        });

        return items;
    }, [cart]);

    // Obtener producto completo desde products array
    const getProduct = useCallback((productId) => {
        return products.find(p => p.id === productId);
    }, [products]);

    // Calcular precio dinámico basado en cantidad total del producto
    const getDynamicUnitPrice = useCallback((productId) => {
        const product = getProduct(productId);
        if (!product || !product.precios) return 0;

        // Sumar todas las cantidades de este producto
        const productItems = expandedCartItems.filter(item => item.productId === productId);
        const totalQty = productItems.reduce((sum, item) => {
            const itemId = item.id;
            const editedQty = editedQuantities[itemId];
            const qty = editedQty !== undefined ? parseInt(editedQty) : item.quantity;
            return sum + qty;
        }, 0);

        if (totalQty === 0) return 0;

        // Buscar el mejor precio para la cantidad total
        const availableQtys = getAvailableQuantities(product);
        let bestPrice = 0;

        for (let qty of availableQtys.sort((a, b) => b - a)) {
            if (totalQty >= qty) {
                bestPrice = getPriceForQuantity(product, qty);
                break;
            }
        }

        return bestPrice || Math.max(...Object.values(product.precios));
    }, [expandedCartItems, editedQuantities, getProduct, getAvailableQuantities, getPriceForQuantity]);

    const totalPrice = useMemo(() => {
        return expandedCartItems.reduce((sum, item) => {
            const itemId = item.id;
            const editedQty = editedQuantities[itemId];
            const qty = editedQty !== undefined ? parseInt(editedQty) : item.quantity;
            const unitPrice = getDynamicUnitPrice(item.productId);
            return sum + (qty * unitPrice);
        }, 0);
    }, [expandedCartItems, editedQuantities, getDynamicUnitPrice]);

    // Actualizar cart cuando cambian las cantidades editadas
    const updateCartAndStorage = useCallback(() => {
        const updatedCart = [...cart];
        const productUpdates = {};

        expandedCartItems.forEach(item => {
            if (!productUpdates[item.productId]) {
                const cartItem = cart.find(ci => ci.productId === item.productId);
                if (cartItem) {
                    productUpdates[item.productId] = {
                        cartItem: { ...cartItem },
                        colors: cartItem.colorName ? cartItem.colorName.split(';') : [''],
                        sizes: cartItem.sizeName ? cartItem.sizeName.split(';') : [''],
                        quantities: cartItem.individualQuantities
                            ? cartItem.individualQuantities.split(';')
                            : [cartItem.quantity.toString()]
                    };
                }
            }
            const update = productUpdates[item.productId];
            if (update) {
                const editedQty = editedQuantities[item.id];
                if (editedQty !== undefined) {
                    update.quantities[item.combinationIndex] = editedQty.toString();
                }
            }
        });

        Object.keys(productUpdates).forEach(productId => {
            const update = productUpdates[productId];
            const cartIndex = updatedCart.findIndex(ci => ci.productId === productId);
            if (cartIndex !== -1) {
                const totalQty = update.quantities.reduce((sum, q) => sum + parseInt(q), 0);
                const unitPrice = getDynamicUnitPrice(productId);
                updatedCart[cartIndex] = {
                    ...update.cartItem,
                    colorName: update.colors.join(';'),
                    sizeName: update.sizes.join(';'),
                    quantity: totalQty,
                    individualQuantities: update.quantities.join(';'),
                    unitPrice,
                    totalPrice: unitPrice * totalQty
                };
            }
        });

        onUpdateCart(updatedCart);
    }, [cart, expandedCartItems, editedQuantities, getDynamicUnitPrice, onUpdateCart]);

    // Debounce para actualizar
    const debouncedUpdate = useMemo(
        () => debounce(updateCartAndStorage, 200),
        [updateCartAndStorage]
    );

    useEffect(() => {
        if (Object.keys(editedQuantities).length > 0) {
            debouncedUpdate();
        }
    }, [editedQuantities, debouncedUpdate]);

    // Manejar cambio de cantidad
    const handleQuantityChange = (itemId, newQuantity) => {
        setEditedQuantities(prev => ({
            ...prev,
            [itemId]: newQuantity
        }));
    };

    // Eliminar una combinación específica
    const handleRemoveCombination = (item) => {
        // Si es un item individual (nuevo formato), simplemente eliminarlo
        if (item.isIndividualItem && item.rowId) {
            const updatedCart = cart.filter(ci => ci.rowId !== item.rowId);
            onUpdateCart(updatedCart);

            // Si no quedan items de este producto, deseleccionar
            const remainingItemsOfProduct = updatedCart.filter(ci => 
                (ci.originalProductId || ci.productId) === item.productId
            );
            
            if (remainingItemsOfProduct.length === 0) {
                const newSelectedProducts = new Set(selectedProducts);
                newSelectedProducts.delete(item.productId);
                onUpdateSelectedProducts(newSelectedProducts);
            }

            // Si el cart queda vacío, cerrar el modal
            if (updatedCart.length === 0) {
                handleClose();
            }

            return;
        }

        // Formato antiguo con punto y coma
        const cartItem = cart.find(ci => ci.productId === item.productId);
        if (!cartItem) return;

        const colors = cartItem.colorName ? cartItem.colorName.split(';') : [''];
        const sizes = cartItem.sizeName ? cartItem.sizeName.split(';') : [''];
        const quantities = cartItem.individualQuantities
            ? cartItem.individualQuantities.split(';')
            : [cartItem.quantity.toString()];

        colors.splice(item.combinationIndex, 1);
        sizes.splice(item.combinationIndex, 1);
        quantities.splice(item.combinationIndex, 1);

        if (colors.length === 0 || quantities.every(q => parseInt(q) === 0)) {
            const updatedCart = cart.filter(ci => ci.productId !== item.productId);
            onUpdateCart(updatedCart);
            const newSelectedProducts = new Set(selectedProducts);
            newSelectedProducts.delete(item.productId);
            onUpdateSelectedProducts(newSelectedProducts);
            if (updatedCart.length === 0) handleClose();
            return;
        }

        const totalQty = quantities.reduce((sum, q) => sum + parseInt(q), 0);
        const unitPrice = getDynamicUnitPrice(item.productId);
        const updatedCartItem = {
            ...cartItem,
            colorName: colors.join(';'),
            sizeName: sizes.join(';'),
            quantity: totalQty,
            individualQuantities: quantities.join(';'),
            unitPrice,
            totalPrice: unitPrice * totalQty
        };

        onUpdateCart(cart.map(ci => ci.productId === item.productId ? updatedCartItem : ci));

        setEditedQuantities(prev => {
            const next = { ...prev };
            delete next[item.id];
            return next;
        });
    };

    // Animación de entrada
    useEffect(() => {
        setIsVisible(true);
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    // Cerrar con animación
    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 300);
    };

    // Confirmar y crear pedido
    const handleConfirm = () => {
        onConfirm();
        handleClose();
    };

    // No renderizar hasta que el contenedor esté listo
    if (!portalContainerRef.current) {
        return null;
    }

    return ReactDOM.createPortal(
        <>
            <div
                className={`cart-preview-modal-overlay ${isVisible ? 'cart-preview-modal-overlay-show' : ''}`}
                onClick={handleClose}
            />
            <div
                ref={modalRef}
                className={`cart-preview-modal-container ${isVisible ? 'cart-preview-modal-container-show' : ''}`}
            >
                {/* Header */}
                <div className="cart-preview-modal-header">
                    <h2 className="cart-preview-modal-title">
                        <FontAwesomeIcon icon={faCheck} className="cart-preview-modal-title-icon" />
                        Resumen del pedido
                    </h2>
                    <button
                        className="cart-preview-modal-close-btn"
                        onClick={handleClose}
                        aria-label="Cerrar"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Lista de items */}
                <div className="cart-preview-modal-items-list">
                    {expandedCartItems.map((item) => {
                        const product = getProduct(item.productId);
                        const availableQtys = product ? getAvailableQuantities(product) : [];
                        const currentQty = editedQuantities[item.id] !== undefined ?
                            editedQuantities[item.id] :
                            item.quantity;
                        const unitPrice = getDynamicUnitPrice(item.productId);
                        const subtotal = currentQty * unitPrice;

                        return (
                            <div key={item.id} className={`cart-preview-modal-item-card ${currentQty === 0 ? 'cart-preview-modal-item-card-incomplete' : ''}`}>
                                <div className="cart-preview-modal-item-image-wrapper">
                                    {item.productImageUrl ? (
                                        <img
                                            src={item.productImageUrl}
                                            alt={item.productName}
                                            className="cart-preview-modal-item-image"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="cart-preview-modal-item-no-image">
                                            <FontAwesomeIcon icon={faImage} />
                                        </div>
                                    )}
                                </div>
                                <div className="cart-preview-modal-item-content">
                                    <div className="cart-preview-modal-item-info">
                                        <h3 className="cart-preview-modal-item-name">{item.productName}</h3>
                                        {(item.color || item.size) && (
                                            <div className="cart-preview-modal-item-variants">
                                                {item.color && (
                                                    <span className="cart-preview-modal-item-variant-tag">
                                                        {item.color}
                                                    </span>
                                                )}
                                                {item.size && (
                                                    <span className="cart-preview-modal-item-variant-tag">
                                                        {item.size}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {currentQty > 0 ? (
                                        <div className="cart-preview-modal-item-bottom">
                                            <div className="cart-preview-modal-item-quantity-wrapper">
                                                {availableQtys.length > 0 ? (
                                                    <select
                                                        className="cart-preview-modal-quantity-select"
                                                        value={currentQty}
                                                        onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                    >
                                                        {availableQtys.map(qty => (
                                                            <option key={qty} value={qty}>
                                                                {qty} {qty === 1 ? 'unidad' : 'unidades'}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span className="cart-preview-modal-item-quantity-badge">
                                                        {currentQty} unidad{currentQty !== 1 ? 'es' : ''}
                                                    </span>
                                                )}
                                                {unitPrice > 0 && (
                                                    <span className="cart-preview-modal-item-unit-price">
                                                        × ${unitPrice.toLocaleString()}
                                                    </span>
                                                )}
                                            </div>
                                            {subtotal > 0 && (
                                                <div className="cart-preview-modal-item-price">
                                                    ${subtotal.toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="cart-preview-modal-item-incomplete-label">
                                            Sin cantidad seleccionada
                                        </div>
                                    )}
                                </div>
                                <button
                                    className="cart-preview-modal-remove-btn"
                                    onClick={() => handleRemoveCombination(item)}
                                    aria-label="Eliminar"
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* Footer con totales y botón de confirmar */}
                <div className="cart-preview-modal-footer">
                    {totalPrice > 0 && (
                        <div className="cart-preview-modal-totals">
                            <div className="cart-preview-modal-total-row cart-preview-modal-total-row-highlight">
                                <span className="cart-preview-modal-total-label">Total estimado:</span>
                                <span className="cart-preview-modal-total-value">${totalPrice.toLocaleString()}</span>
                            </div>
                        </div>
                    )}
                    <button
                        className="cart-preview-modal-confirm-btn"
                        onClick={handleConfirm}
                        disabled={creatingOrder}
                    >
                        <FontAwesomeIcon icon={faWhatsapp} />
                        {creatingOrder ? 'Creando pedido...' : 'Confirmar y enviar por WhatsApp'}
                    </button>
                </div>
            </div>
        </>,
        portalContainerRef.current
    );
};

export default CartPreviewModal;
